import React, { useState, useEffect, useRef } from 'react';
import { Sparkles, CornerDownLeft, Loader2, AlertCircle, FileText } from 'lucide-react';
import { useContact } from '../../context/ContactContext';
import { apiHeaders } from '../../lib/consoleApi';
import { useTranscript } from '../../context/TranscriptContext';
import { pickSuggestion, shouldAutofill, SUGGESTION_LABEL } from '../../lib/copilotSuggestion';

// Agent copilot (D10): a live Q&A box over the tenant knowledge base, backed by
// the knktr-agent-copilot Lambda (Bedrock RetrieveAndGenerate, same KB+guardrail
// as the caller-facing bot). It is REAL-TIME via contact attributes only — it
// never touches Contact Lens or polls for transcripts. On escalation accept the
// box is pre-suggested with what the caller actually wanted (handoverQuestion,
// v9 escalation context 2026-09-10) so the agent can one-tap ask the KB the
// same thing the AI just tried. Before v9 the seed was ai_last_question, which
// on a "speak to a person" escalation IS the escalation utterance — useless as
// a KB query. Contacts without the v9 trigger attribute keep the legacy seed.
const COPILOT_URL = import.meta.env.VITE_COPILOT_URL || '';

function basename(uri) {
  if (!uri) return null;
  return uri.split('/').pop() || uri;
}

export default function Copilot() {
  const { display } = useContact();
  const kbId = display?.knowledgeBaseId ?? null;
  // 2026-10-02: starts from the AI handover question, then follows what the
  // caller says to the agent (lib/copilotSuggestion.js).
  const { lines } = useTranscript();
  const contactId = display?.meta?.contactId ?? null;
  const pick = pickSuggestion(display?.escalation, lines);
  const suggested = pick?.text ?? null;

  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState(null);
  const [citations, setCitations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const autoFilledFor = useRef(null);

  // A new contact starts clean.
  useEffect(() => {
    autoFilledFor.current = null;
    setQuestion(''); setAnswer(null); setCitations([]); setError(null);
  }, [contactId]);

  // Keep the suggestion current, but never overwrite what the agent typed,
  // an answer they are reading, or a lookup in flight.
  useEffect(() => {
    if (shouldAutofill({ suggestion: suggested, question, lastAuto: autoFilledFor.current, hasAnswer: !!answer, loading })) {
      autoFilledFor.current = suggested;
      setQuestion(suggested);
    }
  }, [suggested, question, answer, loading]);

  async function ask() {
    const q = question.trim();
    if (!q || loading) return;
    if (!kbId) { setError('No knowledge base is linked to this contact.'); return; }
    if (!COPILOT_URL) { setError('Copilot endpoint not configured (VITE_COPILOT_URL).'); return; }

    setLoading(true);
    setError(null);
    setAnswer(null);
    setCitations([]);
    try {
      const res = await fetch(COPILOT_URL, {
        method: 'POST',
        headers: await apiHeaders({ 'Content-Type': 'application/json' }),
        // contactId: the server takes the client from the contact, not from this request (backlog 87).
        body: JSON.stringify({ question: q, knowledgeBaseId: kbId, contactId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) {
        throw new Error(data.error || `Lookup failed (HTTP ${res.status}).`);
      }
      setAnswer(data.answer || '(no answer returned)');
      setCitations(Array.isArray(data.citations) ? data.citations : []);
    } catch (err) {
      setError(err?.message || 'Lookup failed — check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }

  function onKeyDown(e) {
    // Enter to ask, Shift+Enter for a newline.
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      ask();
    }
  }

  return (
    <div className="rr-section copilot">
      <div className="rr-section-title copilot-title">
        <Sparkles size={12} />
        Knowledge Search
        {/* 2026-10-20 (Storm 'go labels'): Copilot asks the client's public (customer) knowledge base. */}
        <span className="kb-tier kb-tier-public kb-tier-inline" title="Public. The AI can say this to callers.">Public</span>
      </div>

      {pick && question === pick.text && (
        <div className="copilot-suggest">{SUGGESTION_LABEL[pick.source]}</div>
      )}

      <div className="copilot-ask">
        <textarea
          className="copilot-input"
          placeholder={kbId ? 'Ask the knowledge base…' : 'No knowledge base for this contact'}
          value={question}
          onChange={e => setQuestion(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={!kbId}
          rows={2}
        />
        <button
          className="copilot-ask-btn"
          onClick={ask}
          disabled={!kbId || loading || !question.trim()}
          title="Ask (Enter)"
        >
          {loading ? <Loader2 size={14} className="spin" /> : <CornerDownLeft size={14} />}
          {loading ? 'Asking…' : 'Ask'}
        </button>
      </div>

      {error && (
        <div className="copilot-error">
          <AlertCircle size={13} />
          <span>{error}</span>
        </div>
      )}

      {answer && (
        <div className="copilot-answer">
          <div className="copilot-answer-text">{answer}</div>
          {citations.length > 0 && (
            <div className="copilot-cites">
              {citations.map((c, i) => (
                <div key={i} className="copilot-cite" title={c.snippet || ''}>
                  <FileText size={11} />
                  <span className="copilot-cite-src">{basename(c.source) || 'source'}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
